import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { sendOverlayDismissed, sendOverlayInteraction } from '@/utils/nacxMetrics';
import { logError } from '@/utils/logger';

/**
 * Back/return key codes we may see in `keydown`'s `e.keyCode`:
 * - 461 is the CE-HTML/HbbTV numeric VK_BACK code browsers deliver for the
 *   physical BACK/RETURN button — this is what we normally receive here,
 *   since we're an iframe overlay, not a native Tizen app. It's also what
 *   `CommunicationUtil.onKeyPressed` remaps to 10009 on its way OUT to the
 *   native parent shell (Tizen's own tvinputdevice code for 'Return').
 * - 10009 is included defensively in case some TV/webview variant ever
 *   surfaces the native Tizen code directly to our own listener instead.
 */
const BACK_KEY_CODES = [461, 10009];

/**
 * Composable that wires NACX overlay dismiss/interaction lifecycle metrics.
 *
 * - `markDismissed()` fires dismissed event (user-initiated only, at most once)
 * - `reportBackButtonPressed()` fires the back_button_pressed event (every time)
 * - `trackKeyPress(e)` forwards the key to the SDK (unless suppressed — see
 *   `suppressForwardingKeys` below), then routes to `reportBackButtonPressed()`
 *   for the back key or logs a generic key_press interaction for every other
 *   key — wire this up as your `keydown` handler's first call
 *
 * Note: there's no "closed" event here — when the overlay actually closes is
 * determined by the TV parent, not this app, so we don't track it.
 */
export function useNacxMetrics(
  options: { suppressForwardingKeys?: string[] | (() => string[]) } = {},
) {
  // // Keys the overlay's own navigation fully owns (e.g. arrow/select nav
  // // consumed by useOverlayNavigation or a template's own key handler).
  // // CommunicationUtil.onKeyPressed makes TV Plus act on a key as if it had
  // // been pressed directly on the TV, so we don't forward these while our
  // // overlay is handling them — we still log them below for NACX metrics.
  // // A getter (rather than a fixed list) lets a multi-stage template vary
  // // which keys it owns as its stage changes — see TempPickSellerAndOfferings.
  const { suppressForwardingKeys: suppressForwardingKeysOption } = options;
  const getSuppressedKeys = () =>
    typeof suppressForwardingKeysOption === 'function'
      ? suppressForwardingKeysOption()
      : (suppressForwardingKeysOption ?? []);

  let dismissed = false;

  /** Call when the user explicitly dismisses the overlay. Fires at most once. */
  const markDismissed = () => {
    if (dismissed) return;
    dismissed = true;
    sendOverlayDismissed();
  };

  /**
   * Call when the back button is pressed while the overlay is on screen.
   * Fires every time (not deduplicated) — this feeds a back-press rate, not a
   * one-time flag. entityType/entityValue are TBD; omitted until defined.
   */
  const reportBackButtonPressed = () => {
    sendOverlayInteraction({
      type: 'back_button_pressed',
    });
  };

  /**
   * Call on every key press. Forwards the key to the SDK (unless it's in
   * `suppressForwardingKeys`), then:
   * - back key (keyCode 461 or 10009 — see BACK_KEY_CODES) → `reportBackButtonPressed()` only
   * - every other key → a generic key_press interaction with the key as its value
   *
   * The key_press/back_button_pressed metric always fires regardless of
   * suppression — only the forward to CommunicationUtil.onKeyPressed is gated.
   * Neither path is deduplicated or filtered by overlay visibility.
   */
  const trackKeyPress = (e: KeyboardEvent) => {
    // // onKeyPressed throws synchronously when there's no real/simulated
    // // parent window (e.g. standalone ?mode=full outside a TV harness) —
    // // catch it here so callers' own keydown handling still runs. Mirrors
    // // useOverlayTimeline's existing try/catch around SDK calls for the
    // // same "no parent" scenario.
    if (!getSuppressedKeys().includes(e.key)) {
      try {
        CommunicationUtil.onKeyPressed(e.keyCode);
      } catch (error) {
        logError('[useNacxMetrics] onKeyPressed failed (no parent window?):', error);
      }
    }

    if (BACK_KEY_CODES.includes(e.keyCode)) {
      reportBackButtonPressed();
      return;
    }

    sendOverlayInteraction({
      type: 'key_press',
      entity: [{ type: 'key_press', value: String(e.key) }],
    });
  };

  return { markDismissed, reportBackButtonPressed, trackKeyPress };
}
